import React from 'react';
import { CloseOutlined, PlusOutlined, WarningOutlined } from '@ant-design/icons';

import type { AuthProps } from '../../auth';
import { useAuthDecision } from '../../auth';
import { useUIColors } from '../../theme';
import { useUIText } from '../../i18n';
import { cx, defineRecipe, useRecipe, type Extensible } from '../../variants';

/**
 * `mapped` — a live value. `stale` — the value no longer exists upstream, so
 * it is struck through and flagged. `unmapped` — the value is valid but has no
 * link yet (amber, not struck through — distinct from `stale`, whose link
 * exists but is broken). `add` — the dashed "+" affordance.
 *
 * `Extensible`: an app may add a tone via `UIVariantsProvider` without editing
 * this union.
 */
export type ChipTone = Extensible<'mapped' | 'stale' | 'unmapped' | 'add'>;

/**
 * Three tones × interactive × denied. `denied` is its own axis rather than a
 * fourth tone because it can apply to ANY tone — folding it in would have meant
 * nine members that mostly repeat each other.
 */
export const chipRecipe = defineRecipe('chip', 'ui-chip', {
    variants: {
        tone: {
            mapped: 'ui-chip--mapped',
            stale: 'ui-chip--stale',
            unmapped: 'ui-chip--unmapped',
            add: 'ui-chip--add',
        },
        // `yes`/`no` rather than CVA's boolean `true`/`false` keys: those make
        // the axis a real boolean, and the override registry is string-keyed so
        // an app could not address them. One spelling everywhere beats two.
        interactive: { yes: 'ui-chip--interactive', no: '' },
        denied: { yes: 'ui-chip--denied', no: '' },
    },
    defaultVariants: { tone: 'mapped', interactive: 'no', denied: 'no' },
});

export interface ChipProps extends AuthProps {
    children: React.ReactNode;
    tone?: ChipTone;
    /** Renders the trailing ✕ and makes it actionable. */
    onRemove?: () => void;
    /** Whole-chip click — used by the `add` tone and by editable cells. */
    onClick?: () => void;
    /** Accessible name for the remove control; defaults to the shared catalog. */
    removeLabel?: string;
}

/**
 * EAA-1804: the component sheet's chip — the unit a role map is built from.
 *
 * Ant Design's `Tag` is the closest primitive but the wrong shape here: the
 * sheet's chip is monospace, uses an inset ring rather than a border, and has
 * a dashed "add" variant that must be **exactly the same height** as a real
 * chip. That last point is why the add affordance is this component and not a
 * button — when the last chip in a cell is removed, a differently-sized
 * placeholder makes the whole table row jump.
 *
 * Domain-free by construction: it renders whatever `children` it is handed and
 * knows nothing about role keys or applications.
 */
export function Chip({
    children,
    tone = 'mapped',
    onRemove,
    onClick,
    removeLabel,
    auth,
    // `disable` matches every other interactive atomic in the kit.
    authFallback = 'disable',
}: ChipProps): React.ReactElement {
    const colors = useUIColors();
    const text = useUIText();
    const decision = useAuthDecision(auth, authFallback);

    // Resolved BEFORE the `hide` guard: hooks must run in the same order every
    // render, and this component returns early when auth denies it outright.
    const denied = decision !== 'allow';
    const interactive = Boolean(onClick) && !denied;
    const className = useRecipe(chipRecipe, {
        tone,
        interactive: interactive ? 'yes' : 'no',
        denied: denied ? 'yes' : 'no',
    });

    if (decision === 'hide') return <></>;

    const palettes: Record<string, { bg: string; ring: string; fg: string }> = {
        mapped: {
            bg: colors.chipOkBg,
            ring: colors.chipOkRing,
            fg: colors.chipOkText,
        },
        stale: {
            bg: colors.chipStaleBg,
            ring: colors.chipStaleRing,
            fg: colors.chipStaleText,
        },
        unmapped: {
            bg: colors.warningBg,
            ring: colors.warning,
            fg: colors.warningText,
        },
        add: {
            bg: 'transparent',
            ring: colors.controlRing,
            fg: colors.textSecondary,
        },
    };
    // An app-defined tone paints itself through the class; `mapped` keeps the
    // chip legible in the meantime rather than rendering it invisible.
    const palette = palettes[tone] ?? palettes.mapped;
    const isAdd = tone === 'add';
    const isStale = tone === 'stale';
    const isUnmapped = tone === 'unmapped';

    return (
        <span
            className={cx(className)}
            role={interactive ? 'button' : undefined}
            tabIndex={interactive ? 0 : undefined}
            onClick={interactive ? onClick : undefined}
            onKeyDown={
                interactive
                    ? (event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              onClick?.();
                          }
                      }
                    : undefined
            }
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: onRemove ? 7 : 6,
                padding: '4px 10px',
                borderRadius: 6,
                background: palette.bg,
                // `add` is the one tone drawn with a real (dashed) border; the
                // others use an inset ring so every tone occupies one box size.
                border: isAdd ? `1px dashed ${palette.ring}` : '1px solid transparent',
                boxShadow: isAdd ? undefined : `inset 0 0 0 1px ${palette.ring}`,
                color: palette.fg,
                fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
                fontSize: 13,
                fontWeight: 500,
                lineHeight: 1.4,
                whiteSpace: 'nowrap',
                cursor: interactive ? 'pointer' : 'default',
                opacity: denied ? 0.6 : 1,
            }}
        >
            {isAdd && <PlusOutlined style={{ fontSize: 11 }} />}
            {isUnmapped && <WarningOutlined style={{ fontSize: 12 }} />}
            <span style={{ textDecoration: isStale ? 'line-through' : undefined }}>
                {children}
            </span>
            {isStale && <WarningOutlined style={{ fontSize: 12 }} />}
            {onRemove && !denied && (
                <button
                    type="button"
                    aria-label={removeLabel ?? text.common.delete}
                    onClick={(event) => {
                        event.stopPropagation();
                        onRemove();
                    }}
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: 0,
                        border: 'none',
                        background: 'none',
                        // EAA-1826: `inherit`, never `textLight`. This is an
                        // interactive control, so it gets no placeholder
                        // exemption — #94A1AD is 2.64:1 on white and 2.42:1 on
                        // the chip's own tint, both under WCAG 1.4.11's 3:1 for
                        // UI components. Inheriting takes the tone's own `fg`,
                        // which is contrast-checked against that tone's fill and
                        // keeps a stale chip's × red instead of dropping it to a
                        // grey that reads as a different control. De-emphasis
                        // against the label comes from the 11px size, not from
                        // lowered contrast.
                        color: 'inherit',
                        cursor: 'pointer',
                        fontSize: 11,
                        lineHeight: 1,
                    }}
                >
                    <CloseOutlined />
                </button>
            )}
        </span>
    );
}

export default Chip;
